/**
 * HG Tiered Gifts (GWP) - v18 BEST
 *
 * - 3 configurable tiers
 * - Adds/removes gift products based on qualifying subtotal (pre-discount, excluding gift lines)
 * - Supports stacking vs highest-only
 * - Respects manual removal while qualified until the customer drops below that tier and re-qualifies
 *
 * Pricing (FREE) is handled by Shopify Automatic “Buy X Get Y” discounts per tier.
 * This script only manages cart contents.
 */
(() => {
  const cfg = window.HG_GIFTS || {};
  if (!cfg.enabled) return;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
  const mode = cfg.mode === 'highest' ? 'highest' : 'stack';

  const enabledTiers = tiers
    .map((t) => ({
      tier: toNum(t.tier),
      enabled: Boolean(t.enabled),
      thresholdCents: toNum(t.thresholdCents),
      giftVariantId: toNum(t.giftVariantId),
    }))
    .filter((t) => t.enabled && t.tier >= 1 && t.tier <= 3 && t.thresholdCents > 0 && t.giftVariantId > 0)
    .sort((a, b) => a.thresholdCents - b.thresholdCents);

  if (!enabledTiers.length) return;

  const DISMISSED_PREFIX = 'hg_gifts_dismissed_tier_';

  const getCartJsonUrl = () => {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return `${cartUrl}.js`;
  };

  const fetchCartJson = async () => {
    const res = await fetch(getCartJsonUrl(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error(`[HG Gifts] Failed cart.js (${res.status})`);
    return await res.json();
  };

  const isGiftItem = (item) => {
    const props = item && item.properties;
    if (!props) return false;
    return Object.prototype.hasOwnProperty.call(props, '_hg_gift_tier');
  };

  const getGiftTierValue = (item) => {
    const v = item?.properties?._hg_gift_tier;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const findGiftLineByTier = (cart, tier) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const idx = cart.items.findIndex((it) => isGiftItem(it) && getGiftTierValue(it) === tier);
    if (idx === -1) return null;
    return { item: cart.items[idx], line: idx + 1, key: cart.items[idx]?.key };
  };

  const getQualifyingSubtotalCents = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, it) => {
      if (isGiftItem(it)) return sum;
      return sum + toNum(it.original_line_price);
    }, 0);
  };

  const isDismissed = (tier) => {
    try {
      return sessionStorage.getItem(`${DISMISSED_PREFIX}${tier}`) === '1';
    } catch {
      return false;
    }
  };

  const setDismissed = (tier, val) => {
    try {
      const key = `${DISMISSED_PREFIX}${tier}`;
      if (val) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  };

  const getSectionsToRequest = () => {
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        drawer.getSectionsToRender()
          .map((s) => s.id)
          .filter(Boolean)
          .forEach((id) => ids.add(id));
      } catch {
        // ignore
      }
    }
    return Array.from(ids);
  };

  const publishCartUpdate = async (cartData, source) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      await publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData });
    }
  };

  const updateSectionsFromResponse = (data) => {
    // Prefer Dawn's renderContents when available.
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart && typeof cart.renderContents === 'function' && data?.sections) {
      cart.renderContents(data);
    }
  };

  const addGiftTier = async (tier, giftVariantId) => {
    const sections = getSectionsToRequest();

    const formData = new FormData();
    formData.append('id', String(giftVariantId));
    formData.append('quantity', '1');
    formData.append('properties[_hg_gift_tier]', String(tier));
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    if (config.headers['Content-Type']) delete config.headers['Content-Type'];
    config.body = formData;

    const url = window.routes?.cart_add_url || '/cart/add.js';
    const res = await fetch(url, config);
    const data = await res.json();
    if (data?.status) throw new Error(data.description || data.message || 'Add failed');

    updateSectionsFromResponse(data);
    await publishCartUpdate(data, 'hg-gifts');
  };

  const removeGiftLine = async ({ line, key }) => {
    const sections = getSectionsToRequest();
    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity: 0,
      sections,
      sections_url: window.location.pathname,
    });

    const config = typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json' } };
    config.body = body;

    const url = window.routes?.cart_change_url || '/cart/change.js';
    const res = await fetch(url, config);
    const text = await res.text();
    const parsed = JSON.parse(text);

    // CartItems will handle UI updates via pubsub, but we also try to render immediately for snappiness.
    updateSectionsFromResponse(parsed);
    await publishCartUpdate(parsed, 'hg-gifts');
    return parsed;
  };

  let processing = false;
  let pending = false;
  let scheduled = null;

  const lastPresent = new Map(); // tier -> boolean

  const sync = async () => {
    if (processing) {
      pending = true;
      return;
    }
    processing = true;

    try {
      const cart = await fetchCartJson();
      const qualifying = getQualifyingSubtotalCents(cart);

      // Determine which tiers should be kept
      const qualifiedTiers = enabledTiers.filter((t) => qualifying >= t.thresholdCents).map((t) => t.tier);
      const targetTiers =
        mode === 'highest'
          ? qualifiedTiers.length
            ? [Math.max(...qualifiedTiers)]
            : []
          : qualifiedTiers;

      // Manual removal detection (per tier)
      enabledTiers.forEach((t) => {
        const present = Boolean(findGiftLineByTier(cart, t.tier));
        const was = lastPresent.get(t.tier);
        const isQualified = qualifying >= t.thresholdCents;
        if (isQualified && was === true && present === false) {
          setDismissed(t.tier, true);
        }
        if (!isQualified) setDismissed(t.tier, false);
      });

      // Remove gifts not in target
      for (const t of enabledTiers) {
        const shouldKeep = targetTiers.includes(t.tier);
        const gift = findGiftLineByTier(cart, t.tier);
        if (!gift) continue;
        if (!shouldKeep || qualifying < t.thresholdCents) {
          // Always re-fetch before removal to avoid stale line numbers.
          const fresh = await fetchCartJson();
          const freshGift = findGiftLineByTier(fresh, t.tier);
          if (freshGift) {
            await removeGiftLine({ line: freshGift.line, key: freshGift.key });
          }
        }
      }

      // Add missing gifts that should be present
      for (const t of enabledTiers) {
        const shouldKeep = targetTiers.includes(t.tier);
        if (!shouldKeep) continue;
        if (isDismissed(t.tier)) continue;

        const fresh = await fetchCartJson();
        const giftNow = findGiftLineByTier(fresh, t.tier);
        if (giftNow) continue;

        await addGiftTier(t.tier, t.giftVariantId);
      }

      // Update lastPresent map from final cart state
      const finalCart = await fetchCartJson();
      enabledTiers.forEach((t) => {
        lastPresent.set(t.tier, Boolean(findGiftLineByTier(finalCart, t.tier)));
      });
    } catch (e) {
      console.error('[HG Gifts] sync error', e);
    } finally {
      processing = false;
      if (pending) {
        pending = false;
        // Re-run once if changes happened mid-sync
        await sleep(0);
        sync();
      }
    }
  };

  const scheduleSync = (delayMs = 250) => {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      sync();
    }, delayMs);
  };

  /**
   * UI: Keep gift lines pinned to the top of cart item lists.
   * Shopify orders cart lines by "most recently added first", which can interleave gifts with products.
   * We only adjust DOM order (no cart mutations) so quantity/remove logic remains correct.
   */
  let reorderRaf = 0;
  const reorderGiftLinesInTbody = (tbody) => {
    if (!tbody) return;
    const giftRows = Array.from(tbody.querySelectorAll('tr.cart-item[data-hg-gift="true"]'));
    if (!giftRows.length) return;

    // Stable sort: higher tiers first, preserve original order within same tier
    const withIndex = giftRows.map((row, idx) => ({
      row,
      tier: Number(row.dataset.hgGiftTier || 0),
      idx,
    }));
    withIndex.sort((a, b) => {
      if (b.tier !== a.tier) return b.tier - a.tier;
      return a.idx - b.idx;
    });

    // Avoid unnecessary DOM moves (prevents mutation-observer loops / flicker)
    const allRows = Array.from(tbody.querySelectorAll('tr.cart-item'));
    let needsMove = false;
    for (let i = 0; i < withIndex.length; i++) {
      if (allRows[i] !== withIndex[i].row) {
        needsMove = true;
        break;
      }
    }
    if (!needsMove) return;

    const frag = document.createDocumentFragment();
    withIndex.forEach(({ row }) => frag.appendChild(row));
    tbody.insertBefore(frag, tbody.firstChild);
  };

  const reorderGiftLines = () => {
    // Cart drawer
    reorderGiftLinesInTbody(document.querySelector('#CartDrawer-CartItems tbody'));
    // Main cart page
    reorderGiftLinesInTbody(document.querySelector('#main-cart-items tbody'));
  };

  const scheduleReorderGiftLines = () => {
    if (reorderRaf) return;
    reorderRaf = window.requestAnimationFrame(() => {
      reorderRaf = 0;
      reorderGiftLines();
      // Run again next frame to handle delayed section swaps
      window.requestAnimationFrame(reorderGiftLines);
    });
  };

  // Initial sync
  document.addEventListener('DOMContentLoaded', () => {
    scheduleSync(400);
    scheduleReorderGiftLines();

    // Also observe cart containers for DOM replacement (Sections API)
    if (typeof MutationObserver !== 'undefined') {
      const observe = (el) => {
        if (!el) return;
        const ob = new MutationObserver(() => scheduleReorderGiftLines());
        ob.observe(el, { childList: true, subtree: true });
      };
      observe(document.getElementById('CartDrawer'));
      observe(document.getElementById('main-cart-items'));
    }
  });

  // React to cart updates
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'hg-gifts') return;
      scheduleSync(250);
      scheduleReorderGiftLines();
    });
  }
})();

